/** The colour the game's ground actually is, per terrain.
 *
 * ## Why this table exists at all
 *
 * The editor used to paint its board and its palette swatches out of
 * `TERRAIN_MINIMAP_COLOUR`. That table is right for the minimap and says so in
 * its own header: at one texel per cell the eye reads *region*, so its entries
 * are flattened and desaturated so twelve terrains stay apart from each other
 * and from eight owner colours in a 144px square.
 *
 * At editor scale a cell is 8-64px, and the author is choosing the ground he
 * will get. Measured on `(R+G+B)/3`: the minimap's grass is 92,142,62 -> 98.7,
 * and the grass the adventure map actually renders is 45-52 depending on how
 * much scatter is in the sample. A factor of about 2.1. So an author picked one
 * green and the game gave him another, and no amount of care in the editor
 * could close that.
 *
 * ## Where the numbers come from
 *
 * Each entry is the mean of the sixteen 64x64 positional-variant fill tiles the
 * renderer draws for that terrain - `terrain.<kind>.fill.<ty mod 4>_<tx mod 4>`
 * in `apps/web/public/assets/terrain.json`, which is packed straight from
 * `TILES/assets/terrain/<kind>/master.png`. Measured 2026-08-29, after the
 * grass/water/road regrade (`TILES/tools/regrade_terrain.py`); the sheet and
 * the master art agree to the last tenth, because the packer does not resample.
 *
 * `packages/ui/test/editor-ground.spec.ts` re-measures the art on disk and
 * fails if this table and the shipped tiles drift apart, in either direction:
 * a regrade that moves the art without moving the table would put the editor
 * back where it was, quietly.
 *
 * It earned its keep the afternoon it was written. Another session replaced the
 * cavern master at 15:25 - (101.6, 50.3, 37.1) to (193.9, 148.7, 109.3), a
 * luminance of 63.0 to 150.6 - repacked at 15:30, and rolled it back before
 * 16:20. The guard failed on cavern channel 0 both times, by 91.9 and then by
 * 92.4, which is what a table of numbers copied out of the art looks like when
 * the art moves under it. Anyone editing this table: run the spec, take the
 * number it gives you, and do not trust the comment block below to be newer
 * than the sheet.
 *
 * The cavern grade landed at 17:12 on 2026-08-29, art and table together, and
 * cavern now reads (193.9, 148.7, 109.3) here. That is the shape of the fix
 * for the next one: the art and this literal cannot be moved by two sessions
 * taking turns, because the state between the two moves is red either way.
 * Whoever regrades a fill moves this line in the same change.
 *
 * The board prefers the atlas tiles themselves when the sheet has decoded (see
 * `paintDraft`). This table is what it draws before that happens, what the
 * palette swatches and the minimap-of-the-draft are painted with, and what a
 * cell too small to carry a tile falls back to - so it has to be the same
 * colour the art is, not a colour chosen to look like it.
 */

import type { TerrainKind } from "@game/core";

export type GroundRgb = readonly [number, number, number];

/**
 * Mean RGB of each terrain's sixteen fill tiles, rounded to whole bytes.
 *
 * The measured means, for the record - the rounding below is the only step
 * between these and the table:
 *
 *   cavern    193.9 148.7 109.3   lum 150.62  (regraded 2026-08-29 17:12)
 *   dirt       97.9  70.8  43.2   lum  70.67
 *   grass      53.8  77.9  24.5   lum  52.07
 *   highlands 113.0 109.3  67.9   lum  96.74
 *   lava       69.9  68.2  65.1   lum  67.73
 *   rock       127.3 103.1  85.3   lum 105.22  (re-authored warm stone 2026-09-01)
 *   rough     126.7  99.5  57.6   lum  94.59
 *   sand      223.2 182.6 104.8   lum 170.19
 *   snow      221.4 226.8 228.0   lum 225.41
 *   swamp      55.9  57.1  32.9   lum  48.65
 *   wasteland 117.4  98.7  88.6   lum 101.58
 *   water      24.0  60.4  97.2   lum  60.55
 */
export const TERRAIN_GROUND_COLOUR: Readonly<Record<TerrainKind, GroundRgb>> = Object.freeze({
  water: [24, 60, 97],
  sand: [223, 183, 105],
  dirt: [98, 71, 43],
  grass: [54, 78, 24],
  snow: [221, 227, 228],
  swamp: [56, 57, 33],
  rough: [127, 100, 58],
  cavern: [194, 149, 109],
  lava: [70, 68, 65],
  rock: [127, 103, 85],
  highlands: [113, 109, 68],
  wasteland: [117, 99, 89],
});

/**
 * The atlas key of one positional variant of a terrain's fill.
 *
 * The variant is positional, not random: the renderer picks it from the cell's
 * own coordinates, `v = (ty mod 4) * 4 + (tx mod 4)`, so that the same cell is
 * the same tile on every frame and on every machine. The editor uses the same
 * rule for the same reason plus one more - an author who paints a field and
 * then repaints it must not watch it shuffle.
 */
export const terrainFillKey = (kind: string, cx: number, cy: number): string =>
  `terrain.${kind}.fill.${String(((cy % 4) + 4) % 4)}_${String(((cx % 4) + 4) % 4)}`;

/** Number of positional variants along each axis. Mirrors `variantPeriodTiles`. */
export const FILL_PERIOD = 4;

/**
 * Which of the `FILL_PERIOD^2` fill variants the EDITOR draws for a cell.
 *
 * ## Why this is not `terrainFillKey`'s rule
 *
 * The world renderer picks the variant positionally, `(ty mod 4, tx mod 4)`,
 * and it can: it draws a decal scatter over the top - `render/src/world/scatter.ts`,
 * 650 to 1800 basis points of cells depending on terrain - plus map objects,
 * and those break the four-cell repeat before the eye ever finds it. The editor
 * draws neither, on purpose, because an author needs to see the ground he
 * painted and not a diagram of the clutter that will land on it.
 *
 * So the editor board inherited the repeat with nothing to hide it. Measured on
 * `night/before/editor.1080p.png` - a 48x48 board filled with grass at 32px a
 * cell - the row autocorrelation of the board is +0.999 at a lag of 128 pixels,
 * which is exactly `FILL_PERIOD * 32`: the same sixteen tiles, in the same
 * order, forever. That is wallpaper, and it is what a critic sees first. The
 * same measurement on our own grass in `night/before/adventure.1080p.png`
 * peaked at +0.283. The classic's own grass was quoted at +0.168 off
 * A reference screenshot; **that file does
 * not exist**, and neither does the `night/` scratch pair, though `qa/shoot.mjs`
 * can remake ours.
 *
 * Re-measured 2026-08-30 on a reference screenshot, a genuine the classic
 * adventure frame: over a 100x400 band of open grass the row autocorrelation
 * peaks at **+0.119** (lag 8px, which is short-range texture and not a repeat)
 * and falls to -0.023 at lag 113, the four-tile lag this file's defect lives
 * at. The same statistic on `classic7.jpg`'s sand is -0.069 at its own four-tile
 * lag of 256px. So the number moved from +0.168 to +0.119, and the property
 * that matters moved not at all: **the classic's ground has no periodic peak at any
 * tile multiple**, which is the thing +0.999 was being compared against.
 *
 * A hash of the cell has no period, so there is no lag for the correlation to
 * peak at. It is still a pure function of the cell, which is the property that
 * actually mattered: an author who paints a field and repaints it must not
 * watch it shuffle, and two clients showing the same draft must agree.
 *
 * What it gives up is that two neighbouring cells no longer show two
 * neighbouring slices of the 256px master, so the fill's own internal features
 * do not run on across a cell edge. That is a real cost everywhere except here:
 * this board draws a hard boundary at every cell and a grid over the top,
 * because the author is choosing cells. A seam is what a cell edge already
 * looks like.
 *
 * `terrainFillKey` keeps the positional rule, because the atlas key it builds
 * is also what the palette swatch and the sheet-coverage test ask for.
 */
export const editorFillVariant = (cx: number, cy: number): number => {
  let h = Math.imul(cx | 0, 0x27d4eb2d) ^ Math.imul(cy | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2545f491);
  h ^= h >>> 13;
  return (h >>> 8) % (FILL_PERIOD * FILL_PERIOD);
};

/** The source tile is 64px square; every zoom step is a whole ratio of it. */
export const FILL_TILE_PX = 64;
